import * as yup from '@if/core/yup'
import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  FormBody,
  FormContainerProvider,
  FormSubmitButton,
  useSnackbar,
} from '@if/mui'
import { Stack } from '@mui/material'
import { MockFtfFile, useGenerateMockFtfFile } from '@src/api/admin-commands-api'
import { getToday } from '@src/services/DateTimeProvider'
import { useTranslation } from 'react-i18next'
import CommandCard from './CommandCard'

// Mirrors GenerateMockFtfFileDtoValidator.MaxReturnCount on the backend.
const MAX_RETURN_COUNT = 10000

const schema = yup.object({
  fileDate: yup
    .dateOnly()
    .required()
    .default(() => getToday()),
  returnCount: yup.integer().required().min(1).max(MAX_RETURN_COUNT).default(100),
})

type MockFtfDraft = yup.InferDraft<typeof schema>
type MockFtfValues = yup.InferSubmit<typeof schema>
type MockFtfOutcome = Promise<void | false>

const downloadFile = (file: MockFtfFile) => {
  const url = window.URL.createObjectURL(new Blob([file.content], { type: 'text/plain' }))
  const link = document.createElement('a')
  link.href = url
  link.download = file.fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * Generate an FTF (bank return) file that returns the most recent completed PAD payments
 * before the chosen date, to load-test the bank return processing. The file is only
 * downloaded: nothing is stored and no workflow starts. When fewer payments qualify than
 * requested, the file holds what was found and the snackbar says so.
 */
const GenerateMockFtfFileCard = () => {
  const { t } = useTranslation()
  const { showError, showSuccess, showSnackbar } = useSnackbar()
  const generateMockFtfFile = useGenerateMockFtfFile()

  const handleSubmit = async (values: MockFtfValues): MockFtfOutcome => {
    let file: MockFtfFile
    try {
      file = await generateMockFtfFile.mutateAsync(values)
    } catch {
      showError(t('admin.devTools.messages.error'))
      return false
    }

    downloadFile(file)
    if (file.returnCount < values.returnCount) {
      showSnackbar(
        t('admin.devTools.commands.generateMockFtfFile.partial', {
          generated: file.returnCount,
          requested: values.returnCount,
        }),
        { severity: 'warning' },
      )
      return
    }
    showSuccess(t('admin.devTools.commands.generateMockFtfFile.success', { generated: file.returnCount }))
  }

  return (
    <FormContainerProvider<MockFtfDraft, unknown, MockFtfOutcome, MockFtfValues>
      mode="create"
      useFormSetup={() => ({ schema })}
      onSubmit={handleSubmit}
    >
      <CommandCard
        title={t('admin.devTools.commands.generateMockFtfFile.title')}
        description={t('admin.devTools.commands.generateMockFtfFile.description')}
        actions={<FormSubmitButton label={t('admin.devTools.commands.generateMockFtfFile.actionLabel')} />}
      >
        <FormBody<MockFtfDraft, unknown, MockFtfValues>>
          {({ control }) => (
            <Stack spacing={2}>
              <ControlledDateOnlyPicker
                control={control}
                name="fileDate"
                label={t('admin.devTools.commands.generateMockFtfFile.fileDateLabel')}
              />
              <ControlledNumberField
                control={control}
                name="returnCount"
                label={t('admin.devTools.commands.generateMockFtfFile.returnCountLabel')}
                helperText={t('admin.devTools.commands.generateMockFtfFile.returnCountHelper', {
                  max: MAX_RETURN_COUNT,
                })}
                disableDecimals
                required
              />
            </Stack>
          )}
        </FormBody>
      </CommandCard>
    </FormContainerProvider>
  )
}

export default GenerateMockFtfFileCard
